/**
 * Cutting reconciliation card (F0, `garment.production`) — sits at the top of the
 * Production tab. Shows how many pieces the issued fabric SHOULD yield at the BOM
 * standard vs how many were actually cut, so an over-consumption / shortfall
 * surfaces at the first stage instead of at QC.
 *
 * @see packages/domain/src/garment/wip/compute-cut-plan.ts
 */
import { Scissors } from "lucide-react";
import { trpc } from "../../../lib/trpc.js";

interface CutPlanCardProps {
  styleId: string;
}

export function CutPlanCard({ styleId }: CutPlanCardProps) {
  const query = trpc.wip.cutPlan.useQuery({ styleId });
  const data = query.data;

  if (query.isLoading || !data) return null;
  if (!data.hasFabricBom) return null; // No fabric BOM yet — nothing to reconcile.

  const limiting = data.fabricLines.find((l) => l.limiting) ?? data.fabricLines[0];
  const computable = data.expectedPieces !== null;

  return (
    <div className="rounded-md border border-line bg-bg p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted">
          <Scissors size={12} aria-hidden /> Cutting reconciliation
        </div>
        {computable && data.efficiencyBp !== null ? <YieldChip bp={data.efficiencyBp} /> : null}
      </div>

      {!data.anyIssued ? (
        <p className="mt-2 text-xs leading-relaxed text-muted">
          No fabric issued to cutting yet.
          {limiting ? ` Standard is ${fmtQty(limiting.grossPerPieceMilli, limiting.uom)}/pc (incl. wastage).` : ""}
          {" "}Issue fabric from Materials to see the expected yield.
        </p>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="Expected" value={computable ? `${data.expectedPieces!.toLocaleString("en-IN")} pcs` : "—"} />
            <Metric label="Cut" value={`${data.actualCut.toLocaleString("en-IN")} pcs`} />
            <Metric
              label="Variance"
              value={data.variancePieces === null ? "—" : fmtVariance(data.variancePieces)}
              tone={varianceTone(data.variancePieces)}
            />
            <Metric label="Order" value={`${data.orderQty.toLocaleString("en-IN")} pcs`} muted />
          </div>

          {limiting ? (
            <p className="mt-2 text-xs leading-relaxed text-muted">
              From <strong className="font-medium text-ink-2">{fmtQty(limiting.issuedMilli, limiting.uom)}</strong> issued
              at <strong className="font-medium text-ink-2">{fmtQty(limiting.grossPerPieceMilli, limiting.uom)}/pc</strong>{" "}
              standard{data.fabricLines.length > 1 ? ` · limited by ${limiting.label}` : ` · ${limiting.label}`}.
            </p>
          ) : null}

          {data.fabricLines.length > 1 ? (
            <div className="mt-2 flex flex-col gap-1 border-t border-line pt-2">
              {data.fabricLines.map((l) => (
                <div key={l.bomLineId} className="flex items-center justify-between text-xs">
                  <span className={l.limiting ? "font-medium text-ink" : "text-ink-2"}>
                    {l.label}
                    {l.limiting ? <span className="ml-1 text-warn">· binding</span> : null}
                  </span>
                  <span className="font-mono text-muted">
                    {fmtQty(l.issuedMilli, l.uom)} → {l.expectedPieces === null ? "—" : `${l.expectedPieces} pcs`}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── bits ───────────────────────────────────────────────────────

function Metric({ label, value, tone, muted }: { label: string; value: string; tone?: string | undefined; muted?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className={`font-mono text-lg font-bold ${tone ?? (muted ? "text-ink-2" : "text-ink")}`}>{value}</div>
    </div>
  );
}

function YieldChip({ bp }: { bp: number }) {
  const pct = bp / 100;
  const tone = bp >= 9800 ? "bg-emerald-50 text-ok" : bp >= 9500 ? "bg-amber-50 text-warn" : "bg-red-50 text-bad";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {pct.toLocaleString("en-IN", { maximumFractionDigits: 1 })}% yield
    </span>
  );
}

function fmtVariance(v: number): string {
  if (v === 0) return "on std";
  return v < 0 ? `${Math.abs(v)} short` : `+${v} ahead`;
}

function varianceTone(v: number | null): string | undefined {
  if (v === null) return undefined;
  if (v < 0) return "text-bad";
  return "text-ok";
}

/** milli (× 1000 of the uom) → "1.47 m". */
function fmtQty(milli: number, uom: string): string {
  return `${(milli / 1000).toLocaleString("en-IN", { maximumFractionDigits: 2 })} ${uom}`;
}
